<template>
    <div class="login-container">
        <!-- 背景效果 -->
        <div class="background-effects">
            <div class="floating-shapes">
                <div class="shape shape-1"></div>
                <div class="shape shape-2"></div>
                <div class="shape shape-3"></div>
                <div class="shape shape-4"></div>
            </div>
        </div>
        
        <!-- 登录卡片 -->
        <div class="login-card">
            <div class="login-header">
                <div class="logo-section">
                    <img :src="logoUrl" alt="全域AI" class="logo" />
                </div>
                <h2 class="login-title">欢迎来到{{ PcTitle }}!</h2>
                <p class="login-subtitle">账号密码登录</p>
            </div>
            
            <el-form :model="loginForm" :rules="rules" ref="loginFormRef" class="login-form">
                <el-form-item prop="username">
                    <el-input
                        v-model="loginForm.username"
                        placeholder="请输入账号"
                        size="large"
                        :prefix-icon="User"
                        class="custom-input"
                    />
                </el-form-item>
                
                <el-form-item prop="password">
                    <el-input
                        v-model="loginForm.password"
                        type="password"
                        placeholder="请输入密码"
                        size="large"
                        :prefix-icon="Lock"
                        show-password
                        @keyup.enter="handleLogin"
                        class="custom-input"
                    />
                </el-form-item>
                
                <el-form-item>
                    <el-button
                        type="primary"
                        size="large"
                        class="login-button"
                        :loading="loading"
                        @click="handleLogin"
                    >
                        {{ loading ? '登录中...' : '立即登录' }}
                    </el-button>
                </el-form-item>
            </el-form>
            
            <div class="login-footer">
                <p>没有账号？请联系管理员注册</p>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, inject } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { User, Lock } from '@element-plus/icons-vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config'
import defaultLogo from '@/assets/logo.png' // 导入默认logo


export default {
    name: 'LoginPage',
    setup() {
        const router = useRouter()
        // 注入webConfig
        const webConfig = inject('webConfig', ref({
            pc_title: '全域AI',
            pc_logo: '',
            pc_ico: '',
            copyright_config: []
        }))
        // 计算属性
        const logoUrl = computed(() => webConfig.value.pc_logo || defaultLogo)
        const PcTitle = computed(() => webConfig.value.pc_title || '全域AI')

        const loginForm = ref({
            username: '',
            password: ''
        })

        const loading = ref(false)
        const loginFormRef = ref()

        const rules = {
            username: [
                { required: true, message: '请输入账号', trigger: 'blur' }
            ],
            password: [
                { required: true, message: '请输入密码', trigger: 'blur' }
            ]
        }

        const handleLogin = async () => {
            if (!loginFormRef.value) return
            
            try {
                const valid = await loginFormRef.value.validate()
                if (!valid) return
                
                loading.value = true
                
                const response = await axios.post(
                    `${config.BASE_URL}/store/api/login.php`,
                    {
                        account: loginForm.value.username,
                        password: loginForm.value.password  // 直接发送明文密码
                    }
                )

                if (response.data.code === 200) {
                    Cookies.set('store_user_token', response.data.data.id, { expires: 7 })
                    Cookies.set('store_user_info', JSON.stringify(response.data.data), { expires: 7 })
                    
                    ElMessage.success('登录成功')
                    router.push('/')
                } else {
                    const errorMessages = {
                        400: '请输入完整的账号密码',
                        401: '账号或密码错误',
                        403: response.data.message,
                        500: '服务器错误，请稍后再试'
                    }
                    ElMessage.error(errorMessages[response.data.code] || '登录失败')
                }
            } catch (error) {
                ElMessage.error(error.response ? '登录失败，请重试' : '网络连接异常')
            } finally {
                loading.value = false
            }
        }

        return {
            logoUrl, // 使用计算属性
            PcTitle, // 新增
            loginForm,
            loading,
            loginFormRef,
            rules,
            User,
            Lock,
            handleLogin
        }
    }
}
</script>

<style scoped lang="scss">
.login-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, 
        #0c0e29 0%, 
        #1a1f3d 25%, 
        #252a4a 50%, 
        #1e233f 75%, 
        #141732 100%);
    position: relative;
    overflow: hidden;
    padding: 20px;
}

.background-effects {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: 
            radial-gradient(circle at 20% 80%, rgba(255, 112, 72, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(110, 33, 254, 0.12) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(74, 144, 226, 0.1) 0%, transparent 50%);
        pointer-events: none;
    }
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-image: 
            linear-gradient(rgba(255, 112, 72, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 112, 72, 0.05) 1px, transparent 1px);
        background-size: 50px 50px;
        pointer-events: none;
        animation: gridMove 20s linear infinite;
    }
}

@keyframes gridMove {
    0% {
        transform: translate(0, 0);
    }
    100% {
        transform: translate(50px, 50px);
    }
}

.floating-shapes {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;

    .shape {
        position: absolute;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(255, 112, 72, 0.3), transparent);
        animation: float 6s ease-in-out infinite;
        
        &.shape-1 {
            width: 100px;
            height: 100px;
            top: 20%;
            left: 10%;
            animation-delay: 0s;
        }
        
        &.shape-2 {
            width: 150px;
            height: 150px;
            top: 60%;
            right: 10%;
            animation-delay: 2s;
            background: radial-gradient(circle, rgba(110, 33, 254, 0.25), transparent);
        }
        
        &.shape-3 {
            width: 80px;
            height: 80px;
            bottom: 20%;
            left: 20%;
            animation-delay: 4s;
            background: radial-gradient(circle, rgba(74, 144, 226, 0.2), transparent);
        }
        
        &.shape-4 {
            width: 120px;
            height: 120px;
            top: 30%;
            right: 20%;
            animation-delay: 1s;
            background: radial-gradient(circle, rgba(255, 112, 72, 0.2), transparent);
        }
    }
}

@keyframes float {
    0%, 100% {
        transform: translateY(0) rotate(0deg);
    }
    50% {
        transform: translateY(-20px) rotate(180deg);
    }
}

.login-card {
    width: 420px;
    background: rgba(16, 20, 48, 0.85);
    backdrop-filter: blur(25px);
    border-radius: 20px;
    padding: 50px 40px;
    box-shadow: 
        0 25px 80px rgba(0, 0, 0, 0.6),
        0 0 0 1px rgba(255, 112, 72, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 112, 72, 0.25);
    position: relative;
    z-index: 1;
    animation: cardAppear 0.6s ease-out;

    &::before {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        background: linear-gradient(135deg, 
            rgba(255, 112, 72, 0.4), 
            rgba(110, 33, 254, 0.4), 
            rgba(74, 144, 226, 0.4));
        border-radius: 22px;
        z-index: -1;
        opacity: 0;
        transition: opacity 0.3s ease;
    }

    &:hover::before {
        opacity: 1;
    }
}

@keyframes cardAppear {
    0% {
        opacity: 0;
        transform: translateY(30px) scale(0.95);
    }
    100% {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.login-header {
    text-align: center;
    margin-bottom: 40px;
    
    .logo-section {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        margin-bottom: 20px;

        .logo {
            height: 45px;
            width: auto;
            border-radius: 8px;
        }

        .welcome-text {
            font-size: 24px;
            font-weight: 700;
            background: linear-gradient(135deg, #ff7048 0%, #ff9d76 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            margin: 0;
        }
    }
    
    .login-title {
        color: #e0e6ff;
        font-size: 20px;
        font-weight: 600;
        margin: 0 0 8px 0;
        line-height: 1.4;
    }
    
    .login-subtitle {
        color: #a0a8b8;
        font-size: 14px;
        margin: 0;
        font-weight: 500;
    }
}

.login-form {
    .el-form-item {
        margin-bottom: 28px;
    }
    
    :deep(.custom-input .el-input__wrapper) {
        background: rgba(255, 255, 255, 0.08);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 12px;
        transition: all 0.3s ease;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        padding: 12px 16px;
        
        &:hover {
            border-color: #ff7048;
            box-shadow: 0 6px 20px rgba(255, 112, 72, 0.25);
            transform: translateY(-2px);
        }
        
        &.is-focus {
            border-color: #ff7048;
            box-shadow: 0 8px 25px rgba(255, 112, 72, 0.35);
            transform: translateY(-2px);
        }
    }
    
    :deep(.el-input__inner) {
        color: #e0e6ff;
        background: transparent;
        font-size: 14px;
        
        &::placeholder {
            color: #64748b;
        }
    }
    
    :deep(.el-icon) {
        color: #ff7048;
        font-size: 18px;
    }
}

.login-button {
    width: 100%;
    height: 52px;
    font-size: 16px;
    border-radius: 12px;
    background: linear-gradient(135deg, #ff7048 0%, #ff9d76 100%);
    border: none;
    transition: all 0.3s ease;
    box-shadow: 
        0 6px 20px rgba(255, 112, 72, 0.4),
        inset 0 1px 0 rgba(255, 255, 255, 0.2);
    font-weight: 600;
    letter-spacing: 1px;
    position: relative;
    overflow: hidden;
    
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, 
            transparent, 
            rgba(255, 255, 255, 0.2), 
            transparent);
        transition: left 0.5s ease;
    }
    
    &:hover {
        transform: translateY(-3px);
        box-shadow: 
            0 12px 30px rgba(255, 112, 72, 0.6),
            inset 0 1px 0 rgba(255, 255, 255, 0.3);
        
        &::before {
            left: 100%;
        }
    }
    
    &:active {
        transform: translateY(-1px);
    }
    
    &:disabled {
        transform: none;
        opacity: 0.7;
    }
}

.login-footer {
    text-align: center;
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    
    p {
        color: #a0a8b8;
        font-size: 14px;
        margin: 0;
        font-weight: 500;
    }
}

// 响应式设计
@media (max-width: 480px) {
    .login-container {
        padding: 15px;
    }
    
    .login-card {
        width: 100%;
        max-width: 360px;
        padding: 40px 30px;
        margin: 20px;
    }
    
    .login-header {
        .logo-section {
            .logo {
            height: 45px;
            width: auto;
            border-radius: 8px;
        }
            
            .welcome-text {
                font-size: 20px;
            }
        }
        
        .login-title {
            font-size: 18px;
        }
    }
}

@media (max-width: 360px) {
    .login-card {
        padding: 30px 20px;
    }
    
    .login-header {
        margin-bottom: 30px;
        
        .logo-section {
            flex-direction: column;
            gap: 8px;
            
            .welcome-text {
                font-size: 18px;
            }
        }
        
        .login-title {
            font-size: 16px;
        }
    }
    
    .login-form {
        .el-form-item {
            margin-bottom: 24px;
        }
    }
    
    .login-button {
        height: 48px;
        font-size: 15px;
    }
}
</style>